import {
  ChevronLeft,
  ChevronRight,
  Code,
  FileText,
  Maximize2,
  Minimize2,
  Play
} from 'lucide-react';
import { useEffect, useState } from 'react';

import EditorPage from '../../../../pages/EditorPage';
import { CompilerOutput } from '../../../../pages/ProgrammingTest';
import {
  selectAnswer,
  setAnswers
} from '../../../../redux/Slices/QuestionsSlice';
import { Question } from '../../../../redux/Slices/StartTestSlice/newTemplete';
import { useAppDispatch, useAppSelector } from '../../../../redux/store/hooks';
import {
  useCompileProgramMutation,
  useSendTestActivityMutation
} from '../../../../services/startTestAPI/startTestApi';
import { renderHtmlWithTeX } from '../../../../utils/RenderQuestionWithTex';
import TextField from '../../../Common/TextField';
import QuestionOption from '../QuestionOption';

type QuestionContainerProps = {
  questionNumber: number;
  questionText: string;
  isProgrammingQuestion: boolean;
  options: { answerOption: string; isCorrectAnswer: boolean }[];
  sectionId: string;
  questionId: string;
  actualCorrectOption: string;
  setStartTime: (startTime: Date) => void;
  startTime: Date;
  currentQuestion: Question;
  handleSubmit: () => void;
  handleNext: () => void;
  isLastQuestion: boolean;
};

const QuestionContainer = ({
  questionNumber,
  questionText,
  isProgrammingQuestion,
  options,
  sectionId,
  questionId,
  actualCorrectOption,
  setStartTime,
  startTime,
  currentQuestion,
  handleSubmit,
  handleNext,
  isLastQuestion
}: QuestionContainerProps) => {
  const dispatch = useAppDispatch();
  const selectedAnswer = useAppSelector(
    state => state.root.questions.selectedAnswers[questionNumber] || ''
  );
  const { answers, userTestId } = useAppSelector(state => state.root.questions);
  const {
    userSelectedCompanyTest,
    currentQuestionIndex,
    selectedCodeingLanguage,
    currentCodeingQuestionAnswer
  } = useAppSelector(state => state.root.startFulltest);
  const [sendTestActivity] = useSendTestActivityMutation();
  const [compileProgram] = useCompileProgramMutation();
  const [compileResult, setCompileResult] = useState(null);
  const [isCompiling, setIsCompiling] = useState(false);
  const [maximizedPanel, setMaximizedPanel] = useState<
    'description' | 'code' | null
  >(null);

  useEffect(() => {
    if (!currentCodeingQuestionAnswer || currentCodeingQuestionAnswer === '') {
      setCompileResult(null);
    }
  }, [currentCodeingQuestionAnswer]);

  const handleSelect = (answer: string) => {
    const endTime = new Date();
    setStartTime(endTime);
    const activityData = {
      userTestId,
      startTime,
      endTime,
      companyId: userSelectedCompanyTest?.companyId,
      sectionId: currentQuestion?.sectionId,
      topicId: currentQuestion?.topicId,
      roundId: currentQuestion?.roundId,
      userId: userSelectedCompanyTest?.userId,
      testSetId: userSelectedCompanyTest?.testSetId,
      fromQuestion: currentQuestionIndex + 1,
      toQuestion: currentQuestionIndex + 1,
      event: 'SUBMIT_ANSWER',
      questionId: currentQuestion?.id
    };
    sendTestActivity(activityData);
    const newAnswer = {
      questionId,
      userSelectedOption: answer,
      isCorrectAnswer: answer === actualCorrectOption,
      actualCorrectOption,
      userSubmittedCode: isProgrammingQuestion ? answer : '',
      languageUsed: isProgrammingQuestion ? 'javascript' : '',
      aiGeneratedAnswer: '',
      explanation: ''
    };

    const updatedAnswers = {
      ...answers,
      [sectionId]: answers[sectionId]
        ? [
            ...answers[sectionId].filter(a => a.questionId !== questionId),
            newAnswer
          ]
        : [newAnswer]
    };
    dispatch(selectAnswer({ questionNumber, answer }));
    dispatch(setAnswers({ answers: updatedAnswers }));
  };

  const displayRun =
    selectedCodeingLanguage === 'java' && currentCodeingQuestionAnswer;

  return isProgrammingQuestion ? (
    <>
      <div className='flex w-full items-center justify-center gap-[2px] px-6 py-3'>
        <button
          className={`rounded-l-lg px-3 py-2 text-white ${isCompiling || !displayRun ? 'bg-gray-400' : 'bg-primary'}`}
          disabled={isCompiling || !displayRun}
          onClick={async () => {
            setIsCompiling(true);
            try {
              const res = await compileProgram({
                language: selectedCodeingLanguage?.toUpperCase(),
                code: currentCodeingQuestionAnswer
              }).unwrap();
              setCompileResult(res);
              setIsCompiling(false);
            } catch (error) {
              setIsCompiling(false);
              setCompileResult((error as any)?.data);
            }
          }}
        >
          <Play strokeWidth={1.5} />
        </button>
        <button
          className='rounded-r-lg bg-primary px-3 py-1.5 font-semibold text-white'
          disabled={false}
          onClick={() => {
            isLastQuestion ? handleSubmit() : handleNext();
          }}
        >
          {isLastQuestion ? 'Submit' : 'Next'}
        </button>
      </div>
      <div className='relative flex w-full flex-col justify-center gap-4 px-2 md:px-4 lg:flex-row'>
        <div
          className={`rounded-lg border border-primary transition-all duration-300 lg:h-[67vh] lg:overflow-y-auto
                  ${maximizedPanel === 'code' ? 'lg:w-[4%]' : ''}
                  ${maximizedPanel === 'description' ? 'lg:w-[96%]' : ''}
                  ${!maximizedPanel ? 'lg:w-1/2' : ''}
                  w-full`}
        >
          <div
            className={`flex items-center rounded-t-lg bg-sky-200 p-2 text-sm ${
              maximizedPanel === 'code'
                ? 'h-full flex-col justify-start'
                : 'flex-row justify-between'
            }`}
          >
            <div
              className={`flex items-center gap-2 ${maximizedPanel === 'code' ? 'flex-col' : 'flex-row'}`}
            >
              {!(maximizedPanel === 'code') && (
                <FileText className='h-4 w-4 text-black' />
              )}
              {maximizedPanel === 'code' && (
                <ChevronRight
                  className='mt-2 h-6 w-6 cursor-pointer text-black'
                  onClick={() => setMaximizedPanel(null)}
                />
              )}
              <span
                className={`text-lg font-normal text-black ${
                  maximizedPanel === 'code'
                    ? 'rotate-180 [writing-mode:vertical-rl]'
                    : ''
                }`}
              >
                Description
              </span>
            </div>
            <div
              className={`hidden lg:block ${maximizedPanel === 'code' ? 'hidden' : ''}`}
            >
              {maximizedPanel === 'description' && (
                <Minimize2
                  className='h-4 w-4 cursor-pointer'
                  onClick={() => setMaximizedPanel(null)}
                />
              )}
              {maximizedPanel === null && (
                <Maximize2
                  className='h-4 w-4 cursor-pointer'
                  onClick={() => setMaximizedPanel('description')}
                />
              )}
            </div>
          </div>
          <div
            className={`bg-white px-2 py-6 font-medium text-black lg:px-6 ${maximizedPanel === 'code' ? 'hidden' : 'block'}`}
            dangerouslySetInnerHTML={{
              __html: questionText ?? ''
            }}
          />
        </div>

        <div
          className={`rounded-lg border border-primary transition-all duration-300 lg:overflow-y-auto
                  ${maximizedPanel === 'description' ? 'lg:w-[4%]' : ''}
                  ${maximizedPanel === 'code' ? 'lg:w-[96%]' : ''}
                  ${!maximizedPanel ? 'lg:w-1/2' : ''}
                  ${compileResult && (!maximizedPanel || maximizedPanel === 'code') ? 'lg:h-[45vh]' : 'lg:h-[67vh]'}
                  mb-6 w-full lg:mb-0`}
        >
          <div
            className={`flex items-center rounded-t-lg bg-sky-200 p-2 text-sm ${
              maximizedPanel === 'description'
                ? 'h-full flex-col justify-start'
                : 'flex-row justify-between'
            }`}
          >
            <div
              className={`flex items-center gap-2 ${maximizedPanel === 'description' ? 'flex-col' : 'flex-row'}`}
            >
              {!(maximizedPanel === 'description') && (
                <Code className='h-4 w-4 text-black' />
              )}

              {maximizedPanel === 'description' && (
                <ChevronLeft
                  className='mt-2 h-6 w-6 cursor-pointer text-black'
                  onClick={() => setMaximizedPanel(null)}
                />
              )}

              <span
                className={`text-lg font-normal text-black ${
                  maximizedPanel === 'description'
                    ? 'rotate-180 [writing-mode:vertical-rl]'
                    : ''
                }`}
              >
                Code
              </span>
            </div>
            <div
              className={`hidden lg:block ${maximizedPanel === 'description' ? 'hidden' : ''}`}
            >
              {maximizedPanel === 'code' && (
                <Minimize2
                  className='h-4 w-4 cursor-pointer'
                  onClick={() => setMaximizedPanel(null)}
                />
              )}
              {maximizedPanel === null && (
                <Maximize2
                  className='h-4 w-4 cursor-pointer'
                  onClick={() => setMaximizedPanel('code')}
                />
              )}
            </div>
          </div>
          <div
            className={`mx-auto w-full ${maximizedPanel === 'description' ? 'hidden' : 'block'}`}
          >
            <EditorPage
              questionNumber={questionNumber}
              questionText={questionText}
              questionId={questionId}
              sectionId={sectionId}
              compileResult={compileResult}
            />
          </div>
        </div>
        <div
          className={`absolute -bottom-1 right-1 px-3 py-2  ${maximizedPanel === 'code' ? 'w-[calc(100%-4%-22px)]' : 'w-1/2'} ${maximizedPanel === 'description' ? 'hidden' : 'block'}`}
        >
          <CompilerOutput result={compileResult} />
        </div>
      </div>
    </>
  ) : (
    <div className='mx-2 w-full overflow-auto rounded-lg p-3 font-Primary md:mx-auto md:p-4'>
      <div className='mx-auto flex w-full flex-col justify-center p-2 md:flex-row md:p-4'>
        <div className='flex items-start gap-3 self-stretch text-center md:items-center md:gap-6'>
          <p className='text-sm font-medium text-black md:text-base'>
            {renderHtmlWithTeX(questionText, true)}{' '}
          </p>
        </div>
      </div>
      {options?.length > 0 ? (
        <ul className='mx-auto flex w-full flex-col justify-center gap-2 space-y-2 px-2 md:w-[80%] md:px-0'>
          {options.map((option, index) => (
            <QuestionOption
              key={option.answerOption}
              id={index}
              text={option.answerOption}
              onClick={() => handleSelect(option.answerOption)}
              isSelected={selectedAnswer === option.answerOption}
            />
          ))}
        </ul>
      ) : (
        <div className='mx-auto mt-4 flex w-full flex-col justify-center px-2 md:mt-8 md:w-[80%] md:px-0'>
          <TextField
            handleInputChange={e => handleSelect(e.target.value)}
            value={selectedAnswer}
            placeholder='Enter Answer Here'
            type='text'
            label='Your Answer'
            classes='w-full md:w-[40%]'
          />
        </div>
      )}
    </div>
  );
};

export default QuestionContainer;
